import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { PreviewSlide } from '../models/preview/preview.models';
import { ReportBlockData, ReportBlockSpec } from './report-blocks.service';
import {
	ReportContentItem,
	ReportSettingsModel,
	ReportWidgetOverride
} from './report-settings.service';
import {
	REPORT_TEXT_BLOCK_LIMITS,
	ReportTextBlock
} from './report-text-blocks';
import {
	ReportQuotedFigure,
	figurePlace,
	withPlaceRecord
} from './report-quoted-figures';

/**
 * Typing straight into a report: which texts can be typed into, what a typed
 * edit changes in the settings, the history that undoes it, and the save that
 * follows it.
 *
 * Provided by the preview page and by nothing else, so a page without it — the
 * PDF export, a read-only share — renders the same slides with nothing to type
 * into.
 *
 * Everything the reader types is marked as theirs (`editedByUser` on a widget
 * override, the cover's two flags, `title_edited_by_user` on a chart,
 * `edited_by_user` on a text block). That mark is what lets the editor show
 * "Edited", and what the slide-edit agent reads to leave the reader's words
 * alone unless a request is aimed at them.
 *
 * The same history takes the edits that change what the document holds rather
 * than what a text says — a text block added, turned into another kind, moved
 * or dropped, a chart copied, moved or removed with its handle — so one Undo
 * takes back whichever came last (`restructure`).
 */

/**
 * The longest text each field takes: the bound of the settings field it lands
 * in. A widget override would take 2000 characters on its own, which no heading
 * on an 823px slide survives. Text that is already longer — a summary Ana wrote
 * at length — keeps its length as its bound, so an edit never cuts it.
 */
export const REPORT_INLINE_TEXT_LIMITS = {
	sectionTitle: 120,
	sectionSummary: 500,
	coverTitle: 120,
	coverDescription: 300,
	blockTitle: 120
} as const;

/**
 * The most a summary may hold once its formatting is written out: the bound on
 * a widget override's text. Its 500 characters are counted as the reader sees
 * them, and the markup around them — links above all — needs the room.
 */
const SECTION_SUMMARY_STORED = 2000;

/** Where a typed text goes. */
export type ReportInlineEditTarget =
	| {
			kind: 'section';
			sectionId: string;
			widgetId: string;
			part: 'title' | 'summary';
	  }
	| { kind: 'cover'; part: 'title' | 'description' }
	| { kind: 'block'; block: ReportBlockData }
	| { kind: 'text_block'; id: string };

/** One text the reader can type into, as a slide hands it to its field. */
export interface ReportInlineTextField {
	target: ReportInlineEditTarget;
	/** How much of it the reader can see: the layout's bound. */
	maxLength: number;
	/** Shift+Enter starts a new line. Enter on its own always commits. */
	multiline: boolean;
	/** What assistive technology calls the field. */
	label: string;
	/**
	 * The text is rich text (`report-rich-text.ts`): it is drawn formatted
	 * everywhere, and typed into with the formatting toolbar in the editor.
	 */
	rich?: boolean;
	/** How long the stored text may be with its formatting written out. */
	storedMaxLength?: number;
	/** Emptying the field keeps it empty rather than putting its text back. */
	allowEmpty?: boolean;
}

/** A slide's heading and the text under it. */
export interface ReportSlideTextFields {
	title: ReportInlineTextField | null;
	body: ReportInlineTextField | null;
}

export const NO_SLIDE_TEXT_FIELDS: ReportSlideTextFields = {
	title: null,
	body: null
};

/**
 * The heading and the text under it on a BUILT slide, as fields.
 *
 * Read off the built slide rather than worked out from the section, because the
 * widget ids are minted by each slide as it builds and are what overrides are
 * keyed by — the same ids Ana's edits use, so typing a heading and asking her to
 * rewrite it land on the same override. The cover is the one slide that is not a
 * section: its two texts are the report's own cover fields.
 */
export function slideTextFields(slide: PreviewSlide): ReportSlideTextFields {
	const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
	const shown = (kind: string) =>
		slide.widgets.find(
			widget => widget.kind === kind && !hidden.has(widget.id)
		);

	if (slide.slideType === 'cover_page') {
		return {
			title: shown('title')
				? {
						target: { kind: 'cover', part: 'title' },
						maxLength: REPORT_INLINE_TEXT_LIMITS.coverTitle,
						multiline: false,
						label: 'Report title'
				  }
				: null,
			body: shown('subtitle')
				? {
						target: { kind: 'cover', part: 'description' },
						maxLength: REPORT_INLINE_TEXT_LIMITS.coverDescription,
						multiline: false,
						label: 'Report description'
				  }
				: null
		};
	}

	const title = shown('title');
	const summary = shown('summary_text');
	return {
		title: title
			? {
					target: {
						kind: 'section',
						sectionId: slide.slideType,
						widgetId: title.id,
						part: 'title'
					},
					maxLength: REPORT_INLINE_TEXT_LIMITS.sectionTitle,
					multiline: false,
					label: 'Section title'
			  }
			: null,
		body: summary
			? {
					target: {
						kind: 'section',
						sectionId: slide.slideType,
						widgetId: summary.id,
						part: 'summary'
					},
					maxLength: REPORT_INLINE_TEXT_LIMITS.sectionSummary,
					multiline: true,
					label: 'Section summary',
					rich: true,
					storedMaxLength: SECTION_SUMMARY_STORED
			  }
			: null
	};
}

/**
 * A text block's words, as a field. A heading is one plain line; a paragraph
 * and a callout are rich text, and either may be emptied — an empty block is
 * the one the slash menu opens in.
 */
export function textBlockField(block: ReportTextBlock): ReportInlineTextField {
	const target: ReportInlineEditTarget = { kind: 'text_block', id: block.id };
	if (block.kind === 'heading') {
		return {
			target,
			maxLength: REPORT_TEXT_BLOCK_LIMITS.heading,
			multiline: false,
			label: 'Heading',
			allowEmpty: true
		};
	}
	return {
		target,
		maxLength: REPORT_TEXT_BLOCK_LIMITS.body,
		multiline: true,
		label: block.kind === 'callout' ? 'Callout' : 'Text',
		rich: true,
		storedMaxLength: REPORT_TEXT_BLOCK_LIMITS.stored,
		allowEmpty: true
	};
}

/**
 * A chart's heading, as a field. The numbers under it come from a query and are
 * never typed over; the heading is the reader's to word.
 */
export function blockTitleField(block: ReportBlockData): ReportInlineTextField {
	return {
		target: { kind: 'block', block },
		maxLength: REPORT_INLINE_TEXT_LIMITS.blockTitle,
		multiline: false,
		label: 'Chart title'
	};
}

/**
 * What typed text is kept as: newlines only where the field takes them, runs of
 * spaces collapsed, nothing at either end. Also what a field's own text is
 * compared by, so clicking into a field and out again is never an edit.
 */
export function normalizeInlineText(value: string, multiline: boolean): string {
	const text = value.replace(/\r\n?/g, '\n').replace(/ /g, ' ');
	if (!multiline) {
		return text.replace(/\s+/g, ' ').trim();
	}
	return text
		.split('\n')
		.map(line => line.replace(/[ \t]+/g, ' ').trim())
		.join('\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/**
 * A chart Ana rebuilds keeps the heading the reader typed.
 *
 * `applyReportChart` replaces a spec whole, and that is right for everything
 * the chart MEASURES (see "REPLACE MEANS REPLACE" in the activities). The
 * heading is the reader's words, though: a replacement that leaves the title
 * out keeps theirs, one that repeats it keeps the mark on it, and only a
 * different heading — a rename — replaces it.
 */
export function keepTypedBlockTitle(
	previous: ReportBlockSpec | undefined,
	next: ReportBlockSpec
): ReportBlockSpec {
	if (!previous?.title_edited_by_user || !previous.title) {
		return next;
	}
	if (!next.title) {
		return { ...next, title: previous.title, title_edited_by_user: true };
	}
	return next.title === previous.title
		? { ...next, title_edited_by_user: true }
		: next;
}

/**
 * What the page gives this service. The page owns the report; this service
 * only ever hands it a new copy of the settings and asks it to save.
 */
export interface ReportInlineEditHost {
	/** The report as the page holds it, or null before one has loaded. */
	readonly inlineEditSettings: ReportSettingsModel | null;
	/** The charts as drawn, index for index with the settings' `blocks`. */
	readonly customBlocks: ReportBlockData[];
	/** Put an edited report on the page. The page marks it unsaved. */
	applyInlineEdit(
		settings: ReportSettingsModel,
		customBlocks: ReportBlockData[]
	): void;
	/** The page's own save — the one PUT every change on it goes through. */
	saveInlineEdits(): Observable<ReportSaveAnswer>;
	/**
	 * After a conflict: base the next save on the version the server holds
	 * now, so that it replaces the change made elsewhere.
	 */
	adoptServerVersion(): void;
	/** After a conflict: read the report again, over this page's copy. */
	reloadReport(): void;
	/**
	 * Which number in `text` quotes which figure on the page, for the text
	 * about to be kept at `target` — null when it quotes nothing, undefined
	 * when the page cannot tell and what the place quoted stays as it is.
	 */
	quotedFiguresFor?(
		target: ReportInlineEditTarget,
		text: string
	): ReportQuotedFigure[] | null | undefined;
}

/**
 * How a save ended. `conflict` is the server refusing a copy read before the
 * report last changed — in another tab, or by a chart added from Social
 * Listening — rather than let it write over that change.
 */
export type ReportSaveOutcome = 'saved' | 'failed' | 'conflict';

/**
 * What the page's save answers: an outcome, or `superseded` when the save
 * was for a copy the page has since let go of — Reload, or another report —
 * and so says nothing about the report on it now.
 */
export type ReportSaveAnswer = ReportSaveOutcome | 'superseded';

export type ReportAutosaveStatus =
	| 'idle'
	| 'saving'
	| 'saved'
	| 'failed'
	| 'conflict';

/** How long after the last committed edit the report is written. */
const AUTOSAVE_DELAY_MS = 1000;

/** A session of typing, bounded so the history cannot grow for ever. */
const MAX_HISTORY = 100;

/** A text pinned to where it lives in the settings — what the history keeps. */
type EditLocation =
	| {
			kind: 'section';
			sectionId: string;
			widgetId: string;
			part: 'title' | 'summary';
	  }
	| { kind: 'cover'; part: 'title' | 'description' }
	| { kind: 'block'; index: number; blockId: string }
	| { kind: 'text_block'; id: string };

/** Everything one typed edit changes, so that undoing it puts all of it back. */
interface TextState {
	/** The text itself; null where nothing overrides the generated text. */
	text: string | null;
	/** Whether the reader typed it. */
	edited: boolean;
	/** A section's own `title`, which a typed heading is copied into. */
	mirror?: string;
	/** A chart's drawn heading, which is what shows until it is read again. */
	drawn?: string;
	/**
	 * What the text's numbers quote (report-quoted-figures.ts); null for
	 * nothing, undefined to leave it as it is. Written with the text and put
	 * back with it, so Undo brings a stale number back flagged.
	 */
	figures?: ReportQuotedFigure[] | null;
}

/**
 * What the document holds besides its sixteen sections: the text blocks, and
 * the charts with the blocks drawn for them, index for index — each carrying
 * the place it sits after.
 */
export interface ReportDocumentShape {
	textBlocks: ReportTextBlock[];
	specs: ReportBlockSpec[];
	drawn: ReportBlockData[];
}

/**
 * One step of the history: a typed text, or a change to what the document
 * holds — a text block or a chart added, moved, copied, turned into another
 * kind or dropped. A shape is put back whole, drawn charts and all, so undoing
 * a removed chart draws it again without reading its numbers again.
 */
type HistoryEntry =
	| {
			kind: 'text';
			location: EditLocation;
			before: TextState;
			after: TextState;
	  }
	| {
			kind: 'shape';
			before: ReportDocumentShape;
			after: ReportDocumentShape;
	  };

@Injectable()
export class ReportInlineEditService implements OnDestroy {
	status: ReportAutosaveStatus = 'idle';

	private host: ReportInlineEditHost | null = null;
	private undoStack: HistoryEntry[] = [];
	private redoStack: HistoryEntry[] = [];
	private saveTimer: ReturnType<typeof setTimeout> | null = null;
	private saveInFlight = false;
	/** An edit no save has picked up yet. */
	private dirty = false;
	/** How each run of saves ended, for `saveNow` to wait on. */
	private readonly settled = new Subject<ReportSaveAnswer>();

	attach(host: ReportInlineEditHost): void {
		this.host = host;
		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	/**
	 * Another report is on the page. What was typed into the last one is saved
	 * against the last one first — the page still holds it when this runs — and
	 * none of it can be undone into this one.
	 */
	reset(): void {
		this.flush();
		// What is still unsaved now never can be. Either a save of the last
		// copy is out — nothing more goes until it lands, and by then the page
		// holds another — or the server refused that copy and the reader chose
		// to drop it, with Reload or by leaving. Kept, it would only make the
		// next save write the new copy back for nothing.
		this.dirty = false;
		this.undoStack = [];
		this.redoStack = [];
		this.status = 'idle';
	}

	get canUndo(): boolean {
		return this.undoStack.some(entry => this.holds(entry, 'after'));
	}

	get canRedo(): boolean {
		return this.redoStack.some(entry => this.holds(entry, 'before'));
	}

	/** Whether the reader typed what this field holds now. */
	isEdited(target: ReportInlineEditTarget): boolean {
		const location = this.locate(target);
		return !!(location && this.read(location)?.edited);
	}

	/**
	 * Keep what the reader typed. False when the text has nowhere to go — the
	 * chart it belonged to was removed while it was being typed — and the
	 * field puts back what it showed.
	 */
	commit(field: ReportInlineTextField, text: string): boolean {
		const location = this.locate(field.target);
		const before = location && this.read(location);
		if (!location || !before) {
			return false;
		}

		const after: TextState = { text, edited: true };
		if (location.kind === 'section' && location.part === 'title') {
			after.mirror = fitsSectionTitle(text) ? text : before.mirror;
		}
		if (location.kind === 'block') {
			after.drawn = text;
		}
		// ORDER: before the write — what the place quoted until now is read
		// from the report as it stands.
		const figures = this.host?.quotedFiguresFor?.(field.target, text);
		if (figures !== undefined) {
			after.figures = figures;
		}

		this.write(location, after);
		this.record({ kind: 'text', location, before, after });
		return true;
	}

	/**
	 * Write a number the live data has moved on at its new value — Update, on
	 * the flag under a text — with what the text now quotes. One step of the
	 * history, so Undo brings the old number back; who wrote the text stays
	 * as it was, because a refreshed figure is neither the reader's words nor
	 * Ana's. False when the text is no longer there to write into.
	 */
	refreshFigures(
		target: ReportInlineEditTarget,
		text: string,
		figures: ReportQuotedFigure[]
	): boolean {
		const location = this.locate(target);
		const before = location && this.read(location);
		if (!location || !before || location.kind === 'block') {
			return false;
		}
		const after: TextState = { text, edited: before.edited, figures };
		if (location.kind === 'section' && location.part === 'title') {
			after.mirror = fitsSectionTitle(text) ? text : before.mirror;
		}
		this.write(location, after);
		this.record({ kind: 'text', location, before, after });
		return true;
	}

	/**
	 * Change what the document holds — a text block or a chart added, moved,
	 * copied, turned into another kind or dropped — as one step undo takes
	 * back. What is not passed stays as it is; charts are passed with the
	 * blocks drawn for them, index for index, as the page holds them.
	 */
	restructure(next: Partial<ReportDocumentShape>): void {
		const before = this.shape();
		if (!before) {
			return;
		}
		const after: ReportDocumentShape = { ...before, ...next };
		this.writeShape(after);
		this.record({ kind: 'shape', before, after });
	}

	undo(): void {
		const entry = this.popApplicable(this.undoStack, 'after');
		if (!entry) {
			return;
		}
		this.apply(entry, 'before');
		this.redoStack.push(entry);
		this.scheduleSave();
	}

	redo(): void {
		const entry = this.popApplicable(this.redoStack, 'before');
		if (!entry) {
			return;
		}
		this.apply(entry, 'after');
		this.undoStack.push(entry);
		this.scheduleSave();
	}

	/**
	 * Save a change the page made itself — Ana's, a switched chart type, a
	 * section put in or taken out — on the same delay as a typed one. Every
	 * change to the report saves by itself; nothing waits for a button.
	 */
	requestSave(): void {
		this.scheduleSave();
	}

	retry(): void {
		this.flush();
	}

	/**
	 * Save now instead of in a second, and say how it ended — for a step that
	 * needs the server to hold what the reader sees, like the PDF export, which
	 * renders the saved report. It queues behind a save already in flight
	 * rather than sending its own beside it: two saves based on one version
	 * would have the second refused as a change made elsewhere, by the first.
	 */
	saveNow(): Observable<ReportSaveAnswer> {
		if (this.status === 'conflict') {
			return of<ReportSaveAnswer>('conflict');
		}
		return new Observable<ReportSaveAnswer>(subscriber => {
			const waiting = this.settled.pipe(take(1)).subscribe(subscriber);
			// Unsaved whatever this service last saw: the page can hold a
			// change it has not asked to save yet — a chart still being drawn.
			this.dirty = true;
			this.flush();
			return waiting;
		});
	}

	/**
	 * After a conflict: save this page's copy over the change made elsewhere.
	 * Based on the version the server refused it against, so a report changed
	 * yet again in the meantime is refused again rather than overwritten
	 * unseen.
	 */
	keepMine(): void {
		this.host?.adoptServerVersion();
		this.status = 'saving';
		this.flush();
	}

	/**
	 * After a conflict: put the report as it was saved elsewhere on the page.
	 * What was changed here and not saved is dropped.
	 */
	reload(): void {
		this.host?.reloadReport();
	}

	/**
	 * A write the page made beside its own save — a restore of an earlier
	 * version — was refused as based on an older version. The page is then
	 * where a refused save leaves it: nothing saves until the reader reloads or
	 * keeps their copy, and keeping it writes this copy over the change made
	 * elsewhere. The host holds the version the refusal named.
	 */
	enterConflict(): void {
		this.clearSaveTimer();
		this.dirty = true;
		this.status = 'conflict';
	}

	ngOnDestroy(): void {
		window.removeEventListener('beforeunload', this.onBeforeUnload);
		// Leaving the page is no reason to lose the last second of typing. The
		// save is the page's own request and outlives it.
		this.flush();
	}

	/**
	 * The newest entry that still describes the page, dropping any the page
	 * has moved on from. An entry goes stale when something else rewrote the
	 * same text — Ana, a chart that was removed or moved — and applying it
	 * then would put back words over a change the reader never undid.
	 */
	private popApplicable(
		stack: HistoryEntry[],
		expected: 'before' | 'after'
	): HistoryEntry | null {
		while (stack.length) {
			const entry = stack.pop()!;
			if (this.holds(entry, expected)) {
				return entry;
			}
		}
		return null;
	}

	private record(entry: HistoryEntry): void {
		this.undoStack.push(entry);
		if (this.undoStack.length > MAX_HISTORY) {
			this.undoStack.shift();
		}
		this.redoStack = [];
		this.scheduleSave();
	}

	private apply(entry: HistoryEntry, side: 'before' | 'after'): void {
		if (entry.kind === 'text') {
			this.write(entry.location, entry[side]);
		} else {
			this.writeShape(entry[side]);
		}
	}

	private holds(entry: HistoryEntry, side: 'before' | 'after'): boolean {
		return entry.kind === 'text'
			? this.holdsText(entry.location, entry[side])
			: this.holdsShape(entry[side]);
	}

	private holdsText(location: EditLocation, state: TextState): boolean {
		const current = this.read(location);
		// TRAP: `figures` is left out on purpose. The page records what a text
		// quotes with no step of its own — on a section's first build, on Keep
		// — and comparing it would make every step before that look stale.
		return (
			!!current &&
			current.text === state.text &&
			current.edited === state.edited &&
			(current.mirror ?? null) === (state.mirror ?? null) &&
			(current.drawn ?? null) === (state.drawn ?? null)
		);
	}

	/**
	 * Whether the document still holds exactly `shape`. The drawn charts are
	 * compared as objects, not by their contents: a chart read again is a new
	 * object with numbers of its own, and a shape recorded before that read
	 * would put the old ones back over the ones the reader is looking at.
	 */
	private holdsShape(shape: ReportDocumentShape): boolean {
		const current = this.shape();
		return (
			!!current &&
			JSON.stringify(current.textBlocks) ===
				JSON.stringify(shape.textBlocks) &&
			JSON.stringify(current.specs) === JSON.stringify(shape.specs) &&
			current.drawn.length === shape.drawn.length &&
			current.drawn.every((block, at) => block === shape.drawn[at])
		);
	}

	private shape(): ReportDocumentShape | null {
		const settings = this.host?.inlineEditSettings;
		return settings
			? {
					textBlocks: settings.textBlocks ?? [],
					specs: settings.blocks ?? [],
					drawn: this.host!.customBlocks
			  }
			: null;
	}

	private writeShape(shape: ReportDocumentShape): void {
		const settings = this.host?.inlineEditSettings;
		if (!settings) {
			return;
		}
		this.host!.applyInlineEdit(
			{ ...settings, textBlocks: shape.textBlocks, blocks: shape.specs },
			shape.drawn
		);
	}

	/** A chart is found by where it is drawn; its spec sits at the same index. */
	private locate(target: ReportInlineEditTarget): EditLocation | null {
		if (target.kind !== 'block') {
			return target;
		}
		const index = this.host?.customBlocks.indexOf(target.block) ?? -1;
		const spec =
			index >= 0 ? this.host?.inlineEditSettings?.blocks?.[index] : null;
		return spec ? { kind: 'block', index, blockId: spec.block_id } : null;
	}

	private read(location: EditLocation): TextState | null {
		const settings = this.host?.inlineEditSettings;
		return settings
			? readState(settings, this.host!.customBlocks, location)
			: null;
	}

	private write(location: EditLocation, state: TextState): void {
		const settings = this.host?.inlineEditSettings;
		if (!settings) {
			return;
		}
		this.host!.applyInlineEdit(
			writeState(settings, this.host!.customBlocks, location, state),
			this.host!.customBlocks
		);
	}

	private scheduleSave(): void {
		this.dirty = true;
		// After a conflict an edit stays on the page, unsaved, with the ones
		// before it: they are saved together if the reader keeps theirs.
		if (this.status === 'conflict') {
			return;
		}
		this.status = 'saving';
		this.clearSaveTimer();
		this.saveTimer = setTimeout(() => this.flush(), AUTOSAVE_DELAY_MS);
	}

	/**
	 * Write the report now. One save at a time: an edit made while one is in
	 * flight is picked up when it lands, rather than racing it to the server
	 * with an older copy.
	 */
	private flush(): void {
		this.clearSaveTimer();
		// Nothing goes out in a conflict until the reader chooses — not on a
		// timer, not on the way out of the page: the same copy would only be
		// refused again, and writing over the other change is theirs to decide.
		if (
			!this.dirty ||
			!this.host ||
			this.saveInFlight ||
			this.status === 'conflict'
		) {
			return;
		}
		this.dirty = false;
		this.saveInFlight = true;
		this.status = 'saving';
		this.host.saveInlineEdits().subscribe(outcome => {
			this.saveInFlight = false;
			if (outcome === 'superseded') {
				// About a copy the page has let go of, so nothing here is
				// touched by it; only the edits made to the new copy while it
				// was out still have to go.
				if (this.dirty) {
					this.flush();
				} else {
					this.settled.next(outcome);
				}
				return;
			}
			if (outcome !== 'saved') {
				// A failure waits for Retry; a conflict for the reader's choice.
				this.dirty = true;
				this.status = outcome;
				this.settled.next(outcome);
				return;
			}
			if (this.dirty) {
				this.flush();
				return;
			}
			this.status = 'saved';
			this.settled.next('saved');
		});
	}

	private clearSaveTimer(): void {
		if (this.saveTimer) {
			clearTimeout(this.saveTimer);
			this.saveTimer = null;
		}
	}

	/**
	 * Closing the tab inside the autosave's second, or after a save failed,
	 * would lose typing the reader believes is kept — so the browser asks. A
	 * conflict leaves its changes marked unsaved, so it asks then too, and the
	 * flush before it sends nothing: that copy would only be refused again.
	 */
	private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
		this.flush();
		if (this.dirty || this.saveInFlight || this.status === 'failed') {
			event.preventDefault();
			event.returnValue = '';
		}
	};
}

function fitsSectionTitle(text: string): boolean {
	return text.length <= REPORT_INLINE_TEXT_LIMITS.sectionTitle;
}

function readState(
	settings: ReportSettingsModel,
	blocks: ReportBlockData[],
	location: EditLocation
): TextState | null {
	const state = readText(settings, blocks, location);
	const place = figurePlace(location);
	return state && place
		? { ...state, figures: settings.quotedFigures?.[place] ?? null }
		: state;
}

function readText(
	settings: ReportSettingsModel,
	blocks: ReportBlockData[],
	location: EditLocation
): TextState | null {
	switch (location.kind) {
		case 'section': {
			const item = settings.contentItems.find(
				one => one.id === location.sectionId
			);
			if (!item) {
				return null;
			}
			const override = item.widgetOverrides?.[location.widgetId];
			const state: TextState = {
				text: override?.text ?? null,
				edited: override?.editedByUser === true
			};
			if (location.part === 'title') {
				state.mirror = item.title;
			}
			return state;
		}
		case 'cover':
			return location.part === 'title'
				? {
						text: settings.coverTitle,
						edited: settings.coverTitleEditedByUser === true
				  }
				: {
						text: settings.coverDescription,
						edited: settings.coverDescriptionEditedByUser === true
				  };
		case 'block': {
			const spec = settings.blocks?.[location.index];
			const block = blocks[location.index];
			if (!spec || !block || spec.block_id !== location.blockId) {
				return null;
			}
			return {
				text: spec.title ?? null,
				edited: spec.title_edited_by_user === true,
				drawn: block.title
			};
		}
		case 'text_block': {
			const block = settings.textBlocks?.find(
				one => one.id === location.id
			);
			return block
				? { text: block.text, edited: block.edited_by_user === true }
				: null;
		}
	}
}

function writeState(
	settings: ReportSettingsModel,
	blocks: ReportBlockData[],
	location: EditLocation,
	state: TextState
): ReportSettingsModel {
	const next = writeText(settings, blocks, location, state);
	const place = figurePlace(location);
	return place && state.figures !== undefined
		? {
				...next,
				quotedFigures: withPlaceRecord(
					next.quotedFigures,
					place,
					state.figures
				)
		  }
		: next;
}

function writeText(
	settings: ReportSettingsModel,
	blocks: ReportBlockData[],
	location: EditLocation,
	state: TextState
): ReportSettingsModel {
	switch (location.kind) {
		case 'section':
			return {
				...settings,
				contentItems: settings.contentItems.map(item =>
					item.id === location.sectionId
						? withSectionText(item, location, state)
						: item
				)
			};
		case 'cover':
			return location.part === 'title'
				? {
						...settings,
						coverTitle: state.text ?? settings.coverTitle,
						coverTitleEditedByUser: state.edited || undefined
				  }
				: {
						...settings,
						coverDescription:
							state.text ?? settings.coverDescription,
						coverDescriptionEditedByUser: state.edited || undefined
				  };
		case 'block': {
			const specs = [...(settings.blocks ?? [])];
			const spec: ReportBlockSpec = { ...specs[location.index] };
			if (state.text === null) {
				delete spec.title;
			} else {
				spec.title = state.text;
			}
			if (state.edited) {
				spec.title_edited_by_user = true;
			} else {
				delete spec.title_edited_by_user;
			}
			specs[location.index] = spec;
			// The drawn chart is retitled IN PLACE. A new object would read as a
			// new chart to the page's list, which would tear the canvas down and
			// draw it again for the sake of its heading.
			if (state.drawn !== undefined) {
				blocks[location.index].title = state.drawn;
			}
			return { ...settings, blocks: specs };
		}
		case 'text_block':
			return {
				...settings,
				textBlocks: (settings.textBlocks ?? []).map(block => {
					if (block.id !== location.id) {
						return block;
					}
					const next: ReportTextBlock = {
						id: block.id,
						kind: block.kind,
						text: state.text ?? ''
					};
					if (state.edited) {
						next.edited_by_user = true;
					}
					if (block.after) {
						next.after = block.after;
					}
					// The reader's changes to a text Ana wrote are theirs, and
					// the window its figures were written from is still the one.
					if (block.written) {
						next.written = block.written;
					}
					return next;
				})
			};
	}
}

/**
 * One section with a text of its own. Only the text, who wrote it, and a
 * heading's copy on the section change: a colour the same widget was given is
 * kept, and an override left with nothing in it goes.
 */
function withSectionText(
	item: ReportContentItem,
	location: Extract<EditLocation, { kind: 'section' }>,
	state: TextState
): ReportContentItem {
	const overrides = { ...(item.widgetOverrides ?? {}) };
	const next: ReportWidgetOverride = {
		...(overrides[location.widgetId] ?? {})
	};
	delete next.text;
	delete next.editedByUser;
	if (state.text !== null) {
		next.text = state.text;
	}
	if (state.edited) {
		next.editedByUser = true;
	}

	if (Object.values(next).some(value => value !== undefined)) {
		overrides[location.widgetId] = next;
	} else {
		delete overrides[location.widgetId];
	}

	return {
		...item,
		// A typed heading is copied onto the section too: `title` is what
		// everything that NAMES a section reads — Ana's page hints and her
		// section lookup, the settings form, the reports tool — and a heading
		// the reader can see but Ana cannot find the section by would make her
		// the one thing on the page that disagrees with the slide.
		title: state.mirror ?? item.title,
		widgetOverrides: Object.keys(overrides).length ? overrides : undefined
	};
}
